// Client-side export helpers (CSV + standalone HTML).
import { SYMBOLS, cellToText, formatPrice } from "../lib/matrix";

const csvEscape = (value) => {
  const s = String(value ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const downloadBlob = (content, filename, type) => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const priceOf = (plan, priceMode, symbol) => (plan.price ? formatPrice(plan.price[priceMode], symbol, plan.estimated) : plan.priceLabel || "");

export const buildCSV = (rows, plans, priceMode, pricing = {}) => {
  const symbol = pricing.symbol || "$";
  const header = ["Section", "Group", "Feature", "Level", ...plans.map((p) => p.name)];
  const priceRow = [
    "Pricing",
    "",
    `${pricing.currency || "USD"} per user/month (${priceMode === "teams" ? "with Teams" : "no Teams"})`,
    "",
    ...plans.map((p) => priceOf(p, priceMode, symbol)),
  ];
  const lines = [header, priceRow];
  let currentSection = "";
  let currentSub = "";
  rows.forEach((row) => {
    if (row.type === "section") {
      currentSection = row.title;
      currentSub = "";
      return;
    }
    if (row.type === "sub") {
      currentSub = row.title;
      return;
    }
    lines.push([
      currentSection,
      currentSub,
      row.name,
      row.indent || 0,
      ...plans.map((p) => cellToText(row.cells?.[p.id] || "", "csv")),
    ]);
  });
  return lines.map((l) => l.map(csvEscape).join(",")).join("\n");
};

export const buildHTML = (rows, plans, priceMode, pricing = {}) => {
  const symbol = pricing.symbol || "$";
  const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const colors = { office: "#e82200", ems: "#2080a0", windows: "#060", suite: "#7030a0", related: "#666" };
  const head = `<tr><th style="text-align:left">Feature</th>${plans
    .map((p) => `<th>${esc(p.name)}<br><small>${esc(priceOf(p, priceMode, symbol))}</small></th>`)
    .join("")}</tr>`;
  const body = rows
    .map((row) => {
      if (row.type === "section") {
        return `<tr style="background:${colors[row.cls] || "#666"};color:#fff"><th style="text-align:left">${esc(
          row.title
        )}</th>${plans.map((p) => `<th>${esc(row.header?.[p.id] || "")}</th>`).join("")}</tr>`;
      }
      if (row.type === "sub") {
        return `<tr style="background:#ddd"><th colspan="${plans.length + 1}" style="text-align:left">${esc(row.title)}</th></tr>`;
      }
      const pad = 8 + (row.indent || 0) * 12;
      return `<tr><td style="text-align:left;padding-left:${pad}px;${row.group ? "font-weight:700" : ""}">${esc(
        row.name
      )}</td>${plans
        .map((p) => {
          const raw = row.cells?.[p.id] || "";
          const bg = raw ? "#fff" : "#ececec";
          return `<td style="background:${bg}">${esc(cellToText(raw, "html"))}</td>`;
        })
        .join("")}</tr>`;
    })
    .join("\n");
  return `<!doctype html><html><head><meta charset="utf-8"><title>Microsoft 365 Feature Matrix</title>
<style>body{font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#333;margin:16px}h1{color:#0070f7;font-size:22px}
table{border-collapse:collapse;white-space:nowrap}th,td{border:1px solid #999;padding:3px 8px;text-align:center}th{background:#fff;color:#0070f7}
p{color:#555;font-size:12px}</style></head><body>
<h1>Microsoft 365 Feature Matrix</h1>
<p>${esc(pricing.note || "")}. Effective ${esc(pricing.effective || "")} (${priceMode === "teams" ? "with Teams" : "no Teams"}). Generated ${new Date().toLocaleDateString()}.</p>
<table><thead>${head}</thead><tbody>${body}</tbody></table>
<p>${SYMBOLS.check} included &nbsp; ${SYMBOLS.addon} add-on available &nbsp; ${SYMBOLS.included} included in add-on above &nbsp; ${SYMBOLS.separate} separate purchase</p>
</body></html>`;
};
